import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { clearShopToken, getShopSession } from "@/lib/api";

const IDLE_MS = 20_000;
const WARN_MS = 15_000; // show a toast 5s before the hard logout
const EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];

/**
 * Shop-side idle auto-logout. After IDLE_MS ms with no user activity, clears
 * the shop bearer token and navigates back to /:slug (the shop landing / PIN
 * entry). Call from every authenticated shop page (Input, Admin, Reports, KPI).
 *
 * Super Admin impersonation sessions are exempt — a shop-side idle logout
 * would kick a super admin out mid-troubleshooting, which is not what we want.
 */
export function useShopIdleLogout(slug, message) {
  const nav = useNavigate();
  useEffect(() => {
    if (!slug) return;
    // Skip the timer entirely for Super Admin impersonation sessions.
    const sess = getShopSession(slug);
    if (sess?.is_super_admin) return;
    let hardTimer;
    let warnTimer;
    const idleOut = () => {
      clearShopToken(slug);
      if (message) toast.info(message);
      nav(`/${slug}`);
    };
    const showWarning = () => {
      toast.warning("Signing out in 5 seconds — move the mouse to stay", {
        id: "shop-idle-warning",
        duration: 5_000,
        action: {
          label: "Stay signed in",
          onClick: () => reset(),
        },
      });
    };
    const reset = () => {
      clearTimeout(hardTimer);
      clearTimeout(warnTimer);
      toast.dismiss("shop-idle-warning");
      warnTimer = setTimeout(showWarning, WARN_MS);
      hardTimer = setTimeout(idleOut, IDLE_MS);
    };
    EVENTS.forEach(ev => window.addEventListener(ev, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(hardTimer);
      clearTimeout(warnTimer);
      toast.dismiss("shop-idle-warning");
      EVENTS.forEach(ev => window.removeEventListener(ev, reset));
    };
  }, [slug, message, nav]);
}
